import { Accordion as AccordionPrimitive } from 'radix-ui'
import type * as React from 'react'

import { cn } from '../../lib/utils'

function Accordion({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn('w-full border-t border-border', className)}
      {...props}
    />
  )
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b border-border', className)}
      {...props}
    />
  )
}

/**
 * A `+` / `−` marker, a label, and optional meta pinned to the far end, on
 * ruled rows (`--border` hairlines). Hover is `--accent`; focus is the ink
 * ring drawn inside the row, because the rows touch.
 *
 * The marker is drawn from two spans rather than an icon: at 10px an icon-set
 * plus and minus have different optical weights. It needs no mirroring.
 */
function AccordionTrigger({
  className,
  children,
  meta,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & { meta?: React.ReactNode }) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'group flex min-h-10 flex-1 items-center gap-3 px-1 py-2 text-start text-body font-medium transition-ui',
          'hover:bg-accent',
          'focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-ring',
          className,
        )}
        {...props}
      >
        <span
          aria-hidden="true"
          className="relative inline-block size-2.5 shrink-0 text-muted-foreground"
        >
          <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-current" />
          <span className="absolute inset-y-0 inset-x-0 mx-auto w-px bg-current transition-opacity group-data-[state=open]:opacity-0" />
        </span>
        <span className="flex-1">{children}</span>
        {meta ? <span className="text-small font-normal text-muted-foreground">{meta}</span> : null}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div className={cn('pb-4 ps-7', className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger }
